// Iter 82.11 / Phase E3.2 Sprint 1 — HR Operations Dashboard.
//
// "What does THIS month's payroll look like for HR ops?"
// - Run statuses + variance vs prior month
// - Run readiness counters (Critical / High / Missing-bank / Negative-net / Joiners / Leavers)
// - Delivery health (Generated / Sent / Bounced / OptedOut + bounce rate %)
// - 12-month trend bar chart
// - Top blockers list
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    ChartLineUp, Warning, CheckCircle, Users, Envelope, ArrowRight,
    Bank, ArrowsClockwise,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import PageHeader from "../../components/layout/PageHeader";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
const compact = (n) => {
    const v = Number(n || 0);
    if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
    if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
    if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
    return inr(v);
};
const thisMonthYM = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

function Tile({ label, value, tone, hint, testId, onClick }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={!onClick}
            className={`text-left px-4 py-3 rounded-lg border bg-white ${tone || ""} ${onClick ? "hover:shadow-md hover:border-slate-300 cursor-pointer" : "cursor-default"}`}
            data-testid={testId}
        >
            <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
            <div className="text-lg font-mono font-semibold mt-0.5">{value}</div>
            {hint && <div className="text-[10px] text-slate-400 mt-0.5">{hint}</div>}
        </button>
    );
}

function TrendBars({ series }) {
    const max = Math.max(1, ...series.map((s) => s.net_pay || 0));
    return (
        <div className="flex items-end gap-1 h-24" data-testid="ops-trend-bars">
            {series.map((s) => {
                const h = Math.max(2, (100 * (s.net_pay || 0)) / max);
                const tone = (s.net_pay || 0) > 0 ? "bg-violet-500" : "bg-slate-200";
                return (
                    <div key={s.period_month} className="flex-1 flex flex-col items-center gap-1" title={`${s.period_month}: ${inr(s.net_pay)}`}>
                        <div className={`w-full rounded-t ${tone}`} style={{ height: `${h}%` }} />
                        <div className="text-[9px] text-slate-400 font-mono">{s.period_month.slice(-2)}</div>
                    </div>
                );
            })}
        </div>
    );
}

export default function PayrollOpsDashboard() {
    const nav = useNavigate();
    const [periodMonth, setPeriodMonth] = useState(thisMonthYM());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const reload = useCallback(async () => {
        setLoading(true);
        try {
            const r = await api.get("/payroll/analytics/dashboard/operations", {
                params: { period_month: periodMonth },
            });
            setData(r.data);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load dashboard"));
        } finally {
            setLoading(false);
        }
    }, [periodMonth]);
    useEffect(() => { reload(); }, [reload]);

    // Iter 82.14 / Sprint 4 polish — read-only dashboard deep links.
    // Tile clicks navigate operators into the appropriate operational
    // surface; NEVER mutate state from the dashboard itself.
    const goToRuns = () => nav(`/admin/payroll/runs?period_month=${periodMonth}`);
    const goToWebhooks = () => nav("/admin/payroll/webhooks");
    const goToPeriodClose = () => nav("/admin/payroll/period-close");

    if (loading) return <div className="p-6 text-slate-500" data-testid="ops-loading">Loading dashboard…</div>;
    if (!data) return <div className="p-6 text-rose-700">No data.</div>;

    const t = data.totals || {};
    const r = data.run_readiness || {};
    const d = data.delivery_health || {};
    const variance = data.variance_vs_prior_month_pct || 0;
    const varianceTone = Math.abs(variance) > 5 ? "text-rose-700" : "text-emerald-700";

    return (
        <div className="space-y-4" data-testid="payroll-ops-dashboard">
            <PageHeader
                eyebrow="Payroll Dashboards · HR Operations"
                title={`Operations · ${periodMonth}`}
                description="Run health + delivery health for the current pay period."
                actions={(
                    <div className="flex items-center gap-2">
                        <Input
                            type="month"
                            value={periodMonth}
                            onChange={(e) => setPeriodMonth(e.target.value)}
                            className="h-9 w-40"
                            data-testid="ops-period-picker"
                        />
                        <Button variant="outline" size="sm" onClick={reload} data-testid="ops-refresh-btn">
                            <ArrowsClockwise size={14} className="mr-1" /> Refresh
                        </Button>
                    </div>
                )}
            />

            {/* Totals strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Tile testId="ops-tile-gross" label="Gross Payroll" value={compact(t.gross_earnings)} hint={`${t.headcount || 0} employees · ${t.run_count || 0} runs`} onClick={goToRuns} />
                <Tile testId="ops-tile-net" label="Net Payable" value={compact(t.net_pay)} onClick={goToRuns} />
                <Tile testId="ops-tile-deductions" label="Total Deductions" value={compact(t.gross_deductions)} onClick={goToRuns} />
                <Tile testId="ops-tile-employer" label="Employer Contribs" value={compact(t.employer_contributions)} onClick={goToRuns} />
            </div>

            {/* Variance + Run Readiness */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="border rounded-lg bg-white p-4" data-testid="ops-variance">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Variance vs Prior Month</div>
                    <div className="flex items-baseline gap-2 mt-1">
                        <span className={`text-2xl font-mono font-semibold ${varianceTone}`}>
                            {variance > 0 ? "+" : ""}{Number(variance).toFixed(2)}%
                        </span>
                        <span className="text-xs text-slate-400">vs {data.prior_month || "—"}</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Threshold ±5%</div>
                </div>
                <div className="border rounded-lg bg-white p-4 col-span-2" data-testid="ops-readiness">
                    <div className="text-xs uppercase text-slate-500 tracking-wide mb-2">Run Readiness</div>
                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-center">
                        <div data-testid="ops-readiness-critical">
                            <div className={`text-2xl font-mono ${r.critical_exceptions > 0 ? "text-rose-700" : "text-slate-400"}`}>{r.critical_exceptions || 0}</div>
                            <div className="text-[10px] uppercase text-slate-500">Critical</div>
                        </div>
                        <div data-testid="ops-readiness-high">
                            <div className={`text-2xl font-mono ${r.high_exceptions > 0 ? "text-amber-700" : "text-slate-400"}`}>{r.high_exceptions || 0}</div>
                            <div className="text-[10px] uppercase text-slate-500">High</div>
                        </div>
                        <div data-testid="ops-readiness-missing-bank">
                            <div className={`text-2xl font-mono ${r.missing_bank_count > 0 ? "text-rose-700" : "text-slate-400"}`}>{r.missing_bank_count || 0}</div>
                            <div className="text-[10px] uppercase text-slate-500">Missing Bank</div>
                        </div>
                        <div data-testid="ops-readiness-neg-net">
                            <div className={`text-2xl font-mono ${r.negative_net_blockers > 0 ? "text-rose-700" : "text-slate-400"}`}>{r.negative_net_blockers || 0}</div>
                            <div className="text-[10px] uppercase text-slate-500">Negative Net</div>
                        </div>
                        <div data-testid="ops-readiness-joiners">
                            <div className="text-2xl font-mono text-emerald-700">{r.joiners_in_period || 0}</div>
                            <div className="text-[10px] uppercase text-slate-500">Joiners</div>
                        </div>
                        <div data-testid="ops-readiness-leavers">
                            <div className="text-2xl font-mono text-zinc-600">{r.leavers_in_period || 0}</div>
                            <div className="text-[10px] uppercase text-slate-500">Leavers</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Delivery health */}
            <div className="border rounded-lg bg-white p-4" data-testid="ops-delivery-health">
                <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">Delivery Health</div>
                    <div className="flex gap-2 items-center">
                        <Badge className="bg-emerald-100 text-emerald-800 text-xs" data-testid="ops-delivery-rate">
                            Delivered: {(d.delivery_rate_pct ?? 0).toFixed(2)}%
                        </Badge>
                        <Badge className={`${d.bounce_rate_pct > 5 ? "bg-rose-100 text-rose-800" : "bg-amber-100 text-amber-800"} text-xs`} data-testid="ops-bounce-rate">
                            Bounce rate: {(d.bounce_rate_pct ?? 0).toFixed(2)}%
                        </Badge>
                        <Button variant="outline" size="sm" onClick={goToWebhooks} data-testid="ops-deeplink-webhooks">
                            <ArrowRight size={12} className="mr-1" /> Webhook Ledger
                        </Button>
                    </div>
                </div>
                <div className="grid grid-cols-3 md:grid-cols-9 gap-2 text-center">
                    {["Pending", "Generated", "SendQueued", "Sent", "Delivered", "Bounced", "BouncedSpam", "OptedOut", "Superseded"].map((s) => {
                        const tone = s === "Delivered" ? "bg-emerald-50 text-emerald-800"
                            : s === "Bounced" || s === "BouncedSpam" ? "bg-rose-50 text-rose-800"
                            : "bg-slate-50";
                        return (
                            <div key={s} className={`border rounded px-2 py-2 ${tone}`} data-testid={`ops-delivery-${s}`}>
                                <div className="text-2xl font-mono font-semibold">{d[s] || 0}</div>
                                <div className="text-[10px] uppercase">{s}</div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* 12 month trend */}
            <div className="border rounded-lg bg-white p-4" data-testid="ops-trend">
                <div className="flex items-center justify-between mb-3">
                    <div className="text-xs uppercase text-slate-500 tracking-wide">12-month Net Pay Trend</div>
                    <ChartLineUp size={14} className="text-slate-400" />
                </div>
                <TrendBars series={data.trend_12_months || []} />
            </div>

            {/* Top blockers */}
            <div className="border rounded-lg bg-white p-4" data-testid="ops-top-blockers">
                <div className="text-xs uppercase text-slate-500 tracking-wide mb-2">Top Critical Blockers</div>
                {(data.top_blockers || []).length === 0 ? (
                    <div className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="ops-blockers-empty">
                        <CheckCircle size={14} /> No open Critical blockers this month.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-slate-50 text-xs text-slate-500">
                            <tr><th className="px-3 py-2 text-left">Run</th><th className="px-3 py-2 text-left">Employee</th><th className="px-3 py-2 text-left">Category</th><th className="px-3 py-2 text-left">Message</th></tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {(data.top_blockers || []).map((b, i) => (
                                <tr key={i} className="hover:bg-slate-50" data-testid={`ops-blocker-${i}`}>
                                    <td className="px-3 py-2 text-xs font-mono"><a className="text-blue-600 hover:underline" href={`/admin/payroll/runs/${b.run_id}`}>{b.run_id}</a></td>
                                    <td className="px-3 py-2 text-xs font-mono">{b.employee_id}</td>
                                    <td className="px-3 py-2 text-xs">{b.category}</td>
                                    <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-md">{b.message}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
